import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FileSecret } from '../models/file-secret';
import { Observable, BehaviorSubject } from 'rxjs';
import { SubtleCryptService } from '../services/subtle-crypt.service';
import { saveAs } from 'file-saver';

import {
  trigger,
  state,
  style,
  animate,
  transition,
  keyframes
} from '@angular/animations';

enum FileStatus {
  correctPassword = 1,
  incorrectPassword = 2,
  blankPassword = 3,
  loading = 4
}

@Component({
  selector: 'app-file-item',
  templateUrl: './file-item.component.html',
  styleUrls: ['./file-item.component.css'],
  animations: [
    trigger('slide', [
      state('slideInRight', style({
        visibility: 'visible',
        transform: 'translate3d(100%, 0, 0)'
      })),
      state('slideInRightDone', style({
        transform: 'translate3d(0, 0, 0)'
      })),
      state('slideOutRight', style({
        visibility: 'hidden',
        display: 'none',
        transform: 'translate3d(-100%, 0, 0)'
      })),
      state('bounceOut', style({
        visibility: 'hidden',
        display: 'none'
      })),
      transition('slideInRight => slideInRightDone', [
        animate('0.7s cubic-bezier(0.215, 0.61, 0.355, 1)')
      ]),
      transition('slideInRightDone => bounceOut', [
        animate('750ms', keyframes([
          style({transform: 'scale3d(0.9, 0.9, 0.9)', offset: 0.2}),
          style({transform: 'scale3d(1.1, 1.1, 1.1)', opacity: 1, offset: 0.5}),
          style({transform: 'scale3d(1.1, 1.1, 1.1)', opacity: 1, offset: 0.55}),
          style({transform: 'scale3d(0.3, 0.3, 0.3)', opacity: 0, display: 'none', visibility: 'hidden', offset: 1})
        ]))
      ]),
      state('shake', style({
        transform: 'translate3d(0, 0, 0)'
      })),
      transition('* => shake', [
        animate('1s', keyframes([
          style({transform: 'translate3d(0, 0, 0)', offset: 0.0}),
          style({transform: 'translate3d(-10px, 0, 0)', offset: 0.1}),
          style({transform: 'translate3d(10px, 0, 0)', offset: 0.2}),
          style({transform: 'translate3d(-10px, 0, 0)', offset: 0.3}),
          style({transform: 'translate3d(10px, 0, 0)', offset: 0.4}),
          style({transform: 'translate3d(-10px, 0, 0)', offset: 0.5}),
          style({transform: 'translate3d(10px, 0, 0)', offset: 0.6}),
          style({transform: 'translate3d(-10px, 0, 0)', offset: 0.7}),
          style({transform: 'translate3d(10px, 0, 0)', offset: 0.8}),
          style({transform: 'translate3d(-10px, 0, 0)', offset: 0.9}),
          style({transform: 'translate3d(0, 0, 0)', offset: 1.0}),
        ]))
      ])
    ])
  ]
})
export class FileItemComponent implements OnInit {

  @Input('file') file;

  public isLoadingObs: Observable<boolean>
  private isLoadingSubject: BehaviorSubject<boolean>

  public buffer:  ArrayBuffer;

  public isEncrypted: boolean;

  public deleted: boolean;

  public placeholder: string;

  public encoded: string;

  public status: FileStatus = FileStatus.blankPassword;

  private FILE_SIZE_LIMIT:number = 20000000; // 20mb

  private salt:string = "#fT29yy1JktNqcV%Jp71Wz&^K1Vw40N!"; // SALT doesn't need to be kept secret.

  private extension:string = ".wbc";

  private checksumBytes: number[] = 
  [
      0xd8, 0xbf, 0x06, 0x81,
      0x9b, 0x4f, 0xe3, 0xe2, 
      0x8f, 0xff, 0x73, 0x38, 
      0x65, 0xce, 0xcd, 0xba
  ]

  @Output()
  deleteFile: EventEmitter<any> = new EventEmitter<any>();

  private checksum:Uint8Array = new Uint8Array(this.checksumBytes.length);

  constructor(private subtleCrypt: SubtleCryptService) { }

  password: FileSecret = {
    value: ""
  }

  readFile(file: File): Promise<ArrayBuffer> {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file); // readArrayAsBuffer(<Blob | File>)
    return new Promise((resolve) => {
      reader.onload = () => { 
        resolve(<ArrayBuffer>reader.result);
      }
    });
  }
  
  encodeBufferAsString(buffer: ArrayBuffer): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        if (buffer.byteLength > this.FILE_SIZE_LIMIT) { // > 20mb
          reject('File too big! Exceeds limit of ' + this.FILE_SIZE_LIMIT + ' bytes.');
        } else {
          resolve(new Uint8Array(buffer).reduce(
            (data, byte) => data + String.fromCharCode(byte), '')
          );
        }
      } catch(e) {
        reject('Something went wrong during encoding.');
      }
    });
  }

  encodeBuffer(buffer: ArrayBuffer): Promise<string> {
    return new Promise((resolve) => {
      this.encodeBufferAsString(buffer).then(str => {
        resolve(btoa(str));
      }).catch(e => alert(e));
    });
  }

  decodeStringToBuffer(str: string): Promise<Uint8Array> {
    return new Promise((resolve) => {
        resolve(new Uint8Array(atob(str).split('').map(x => x.charCodeAt(0))));
    });
  }

  remove(): void {
    this.deleted = true;
    setTimeout(() => {
      this.deleteFile.emit(this.file)
    }, 800)
  }

  passwordFinish(value: String) {
    if (value && value != '') {
      if (this.buffer) {
        var bitEntropy = 256;
        var ivLength:number = 16;

        if (!this.isEncrypted) {
          var iv:Uint8Array = this.subtleCrypt.getRandomIV(ivLength);
          this.subtleCrypt.encrypt(
            this.buffer,
            iv,
            value,
            this.salt,
            bitEntropy
            ).then(data => {
              saveAs(new Blob([this.checksum, iv, data]), this.file.name + this.extension);
              this.status = FileStatus.correctPassword;
              this.remove();
            }).catch(error => {
              this.status = FileStatus.incorrectPassword;
            });
        } else {
          this.subtleCrypt.decrypt(
            this.buffer.slice(ivLength + this.checksumBytes.length, this.buffer.byteLength), // data
            this.buffer.slice(this.checksumBytes.length, this.checksumBytes.length + ivLength), // iv
            value,
            this.salt,
            bitEntropy
            ).then(data => {
              var name:string = this.file.name;
              if (name.slice(-4) == this.extension) {
                name = this.file.name.substring(0, this.file.name.length - 4);
              }
              saveAs(new Blob([data]), name);
              this.status = FileStatus.correctPassword;
              this.remove();
            }).catch(error => {
              this.status = FileStatus.incorrectPassword;
            });
        }
      }
    } else {
      this.status = FileStatus.blankPassword;
    }
  }

  passwordChanged(event) {
    if (event.key.length == 1) {
      this.status = FileStatus.loading;
    }
  }

  ngOnInit() {
    this.checksum.set(this.checksumBytes, 0);
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoadingObs = this.isLoadingSubject.asObservable();
    this.deleted = false;
    this.encoded = null;

    this.readFile(this.file).then(buffer => { 
      this.buffer = buffer;

      // Does the checksum match?
      this.isEncrypted =  <boolean>(() => { 
        if (buffer.byteLength < this.checksumBytes.length)
          return false
        
        var slice = new Uint8Array(buffer.slice(0, this.checksumBytes.length));
        for(var i = 0; i < this.checksum.length; i++) {
          if (this.checksumBytes[i] != slice[i]) {
            return false;
          }
        }
        return true;
      })();

      /*if (this.isEncrypted) {   
        setTimeout(() => {
          // Provide a base64 url after 5 seconds
          this.encodeBuffer(buffer).then(encoded => {
            this.encoded = encoded;
          });
        }, 1500);
      }*/

      this.placeholder = (this.isEncrypted ? "Decryption password" : "Encryption password");
    });
  }

}
